import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Trophy, RefreshCw, Settings, Satellite, AlertTriangle, ChevronDown, ChevronUp, Minus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const EMPTY_CFG = { temporada: "", competicion: "", grupo: "", tipo_juego: "", nombre_oficial: "", cod_equipo: "", url: "" };

function TrendArrow({ trend }) {
  if (!trend || trend.direction === "new") return null;
  const d = trend.direction;
  const title = d === "up" ? `Sube ${trend.delta} puesto${trend.delta !== 1 ? "s" : ""} respecto a la J${trend.prev_jornada}`
    : d === "down" ? `Baja ${trend.delta} puesto${trend.delta !== 1 ? "s" : ""} respecto a la J${trend.prev_jornada}`
    : `Mantiene la posición respecto a la J${trend.prev_jornada}`;
  const Icon = d === "up" ? ChevronUp : d === "down" ? ChevronDown : Minus;
  const cls = d === "up" ? "text-emerald-600" : d === "down" ? "text-rose-600" : "text-slate-400";
  return (
    <span className={`inline-flex items-center ${cls}`} title={title} data-testid={`standings-trend-${d}`}>
      <Icon className="h-3.5 w-3.5" />{d !== "same" && <span className="text-[9px] font-bold">{trend.delta}</span>}
    </span>
  );
}

export default function LeagueStandings({ teamId, canEdit, isPreseason }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CFG);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/teams/${teamId}/standings`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teamId]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/teams/${teamId}/standings/refresh`);
      setData(r.data);
      if (r.data.status === "ok") toast.success("Clasificación actualizada");
      else if (r.data.status === "stale") toast.error("No se pudo actualizar; se muestra la última disponible");
      else toast.error("No se pudo obtener la clasificación");
    } catch (e) { toast.error("Error al actualizar"); }
    finally { setRefreshing(false); }
  };

  const openConfig = () => { setForm({ ...EMPTY_CFG, ...(data?.config || {}) }); setCfgOpen(true); };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/teams/${teamId}/standings/config`, form);
      setData(r.data);
      setCfgOpen(false);
      toast.success(r.data.status === "ok" ? "Vinculación guardada y clasificación cargada" : "Vinculación guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la vinculación");
    } finally { setSaving(false); }
  };

  const header = (
    <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm font-black uppercase tracking-wide text-slate-700 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-purple-600" /> Clasificación de liga
        {data?.jornada && <span className="text-[11px] font-semibold text-slate-400 normal-case">· Jornada {data.jornada}</span>}
      </CardTitle>
      <div className="flex items-center gap-1.5">
        {canEdit && (data?.status !== "not_configured") && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={refresh} disabled={refreshing} data-testid="standings-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        )}
        {canEdit && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={openConfig} data-testid="standings-config">
            <Settings className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </CardHeader>
  );

  const rows = data?.rows || [];

  const body = () => {
    if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>;
    if (!data || data.status === "not_configured") {
      return (
        <div className="py-6 text-center" data-testid="standings-not-configured">
          <p className="text-sm text-slate-500">Este equipo no está vinculado a una clasificación oficial.</p>
          {canEdit && <Button size="sm" className="mt-3 bg-purple-600 hover:bg-purple-700 text-white" onClick={openConfig} data-testid="standings-configure-btn">Vincular con RFFM</Button>}
        </div>
      );
    }
    if (rows.length === 0) {
      return <p className="py-6 text-center text-sm text-slate-500" data-testid="standings-pending">Clasificación pendiente de sincronizar.</p>;
    }
    return (
      <>
        {data.status === "stale" && (
          <div className="flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-2" data-testid="standings-stale">
            <AlertTriangle className="h-3.5 w-3.5" /> No se ha podido actualizar; se muestra la última clasificación disponible.
          </div>
        )}
        {isPreseason && (
          <p className="text-[11px] text-slate-500 italic mb-2" data-testid="standings-preseason-note">
            La clasificación mostrada pertenece a la competición oficial (no se ve afectada por el filtro de pretemporada).
          </p>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8 px-1 text-center">#</TableHead>
                <TableHead>Equipo</TableHead>
                <TableHead className="text-center w-10">PJ</TableHead>
                <TableHead className="text-center w-8 hidden md:table-cell">G</TableHead>
                <TableHead className="text-center w-8 hidden md:table-cell">E</TableHead>
                <TableHead className="text-center w-8 hidden md:table-cell">P</TableHead>
                <TableHead className="text-center w-10 hidden md:table-cell">GF</TableHead>
                <TableHead className="text-center w-10 hidden md:table-cell">GC</TableHead>
                <TableHead className="text-center w-10 hidden md:table-cell">DG</TableHead>
                <TableHead className="text-center w-12 font-black">Pts</TableHead>
                <TableHead className="w-6 px-0 md:hidden"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const own = data.own_code && r.codequipo === data.own_code;
                const dg = (r.goles_favor ?? 0) - (r.goles_contra ?? 0);
                const isOpen = expanded === r.codequipo;
                return (
                  <React.Fragment key={r.codequipo || r.posicion}>
                    <TableRow
                      className={`${own ? "bg-purple-100 hover:bg-purple-100 font-black text-purple-900" : "hover:bg-slate-50"} cursor-pointer md:cursor-default`}
                      onClick={() => setExpanded(isOpen ? null : r.codequipo)}
                      data-testid={`standings-row-${r.posicion}${own ? "-own" : ""}`}>
                      <TableCell className="px-1 text-center font-bold">
                        <span className="inline-flex items-center gap-0.5">{r.posicion}{own && <TrendArrow trend={data.trend} />}</span>
                      </TableCell>
                      <TableCell className="truncate max-w-[52vw] md:max-w-none">
                        {r.nombre}{own && <span className="ml-1.5 text-[9px] bg-purple-600 text-white rounded px-1 py-0.5 align-middle">TU EQUIPO</span>}
                      </TableCell>
                      <TableCell className="text-center">{r.jugados}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{r.ganados}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{r.empatados}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{r.perdidos}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{r.goles_favor}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{r.goles_contra}</TableCell>
                      <TableCell className="text-center hidden md:table-cell">{dg > 0 ? `+${dg}` : dg}</TableCell>
                      <TableCell className="text-center font-black">{r.puntos}</TableCell>
                      <TableCell className="px-0 md:hidden text-slate-400">
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className={`md:hidden ${own ? "bg-purple-50" : "bg-slate-50"}`} data-testid={`standings-detail-${r.posicion}`}>
                        <TableCell colSpan={5} className="text-[11px] text-slate-600 py-1.5">
                          <span className="inline-flex gap-3 flex-wrap">
                            <span>G <b>{r.ganados}</b></span>
                            <span>E <b>{r.empatados}</b></span>
                            <span>P <b>{r.perdidos}</b></span>
                            <span>GF <b>{r.goles_favor}</b></span>
                            <span>GC <b>{r.goles_contra}</b></span>
                            <span>DG <b>{dg > 0 ? `+${dg}` : dg}</b></span>
                          </span>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-2 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><Satellite className="h-3 w-3" /> Fuente: {data.source || "RFFM"}</span>
          <span data-testid="standings-last-updated">Última actualización: {fmtDT(data.last_updated)}</span>
        </div>
      </>
    );
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="section-league-standings">
      {header}
      <CardContent>{body()}</CardContent>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-md" data-testid="standings-config-dialog">
          <DialogHeader><DialogTitle className="text-base">Vincular con la clasificación RFFM</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Pega la <b>URL oficial</b> de la clasificación/jornadas del grupo en RFFM y se autocompletarán los códigos. También puedes rellenarlos a mano.
          </p>
          <div className="space-y-2">
            <div>
              <label className="text-[11px] font-bold text-slate-600">URL oficial RFFM</label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://www.rffm.es/competicion/clasificaciones?temporada=..." data-testid="cfg-url" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[11px] font-bold text-slate-600">Temporada</label>
                <Input value={form.temporada} onChange={(e) => setForm({ ...form, temporada: e.target.value })} data-testid="cfg-temporada" /></div>
              <div><label className="text-[11px] font-bold text-slate-600">Competición</label>
                <Input value={form.competicion} onChange={(e) => setForm({ ...form, competicion: e.target.value })} data-testid="cfg-competicion" /></div>
              <div><label className="text-[11px] font-bold text-slate-600">Grupo</label>
                <Input value={form.grupo} onChange={(e) => setForm({ ...form, grupo: e.target.value })} data-testid="cfg-grupo" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] font-bold text-slate-600">Nombre oficial del equipo</label>
                <Input value={form.nombre_oficial} onChange={(e) => setForm({ ...form, nombre_oficial: e.target.value })}
                  placeholder="ESCUELA DE FUTBOL CONCEPCION" data-testid="cfg-nombre" /></div>
              <div><label className="text-[11px] font-bold text-slate-600">Cód. equipo (opcional)</label>
                <Input value={form.cod_equipo} onChange={(e) => setForm({ ...form, cod_equipo: e.target.value })} data-testid="cfg-codequipo" /></div>
            </div>
            <p className="text-[10px] text-slate-400">El nombre oficial o el código sirven para resaltar tu equipo en la tabla.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCfgOpen(false)} data-testid="cfg-cancel">Cancelar</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={saveConfig} disabled={saving} data-testid="cfg-save">
              {saving ? "Guardando…" : "Guardar y cargar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
